import { z } from 'zod';
import { propertyFactsSchema } from './property-facts.js';
const paise = z.number().int().min(0).max(Number.MAX_SAFE_INTEGER);
const text = (max) => z.string().trim().min(1).max(max);
export const PROPERTY_KINDS = ['rent', 'sale'];
export const FURNISHINGS = ['unfurnished', 'semi_furnished', 'furnished'];
export const MAX_PHOTOS = 10;
const propertyFields = {
    kind: z.enum(PROPERTY_KINDS),
    title: text(120),
    branchId: z.string().uuid().optional(),
    bhk: z.number().int().min(0).max(20).nullable().optional(),
    locality: text(80).nullable().optional(),
    society: text(80).nullable().optional(),
    flatNo: text(30).nullable().optional(),
    floor: text(20).nullable().optional(),
    carpetSqft: z.number().int().min(1).max(100_000).nullable().optional(),
    furnishing: z.enum(FURNISHINGS).nullable().optional(),
    rentPaise: paise.nullable().optional(),
    pricePaise: paise.nullable().optional(),
    depositPaise: paise.nullable().optional(),
    maintenancePaise: paise.nullable().optional(),
    availableFrom: z
        .string()
        .regex(/^\d{4}-\d{2}-\d{2}$/)
        .nullable()
        .optional(),
    preferredTenants: text(200).nullable().optional(),
    amenities: z.array(text(40)).max(30).optional(),
    ownerName: text(80).nullable().optional(),
    ownerPhone: text(20).nullable().optional(),
    ownerLang: z.enum(['en', 'mr', 'hi']).optional(),
    tellOwner: z.boolean().optional(),
    notes: z.string().trim().max(2000).nullable().optional(),
    facts: propertyFactsSchema.optional(),
};
export const createPropertySchema = z
    .object(propertyFields)
    .refine((value) => (value.kind === 'rent' ? value.rentPaise != null : value.pricePaise != null), {
    message: 'A rent listing needs a rent and a sale listing needs a price',
    path: ['rentPaise'],
});
export const updatePropertySchema = z
    .object(propertyFields)
    .omit({ kind: true, branchId: true })
    .partial()
    .refine((value) => Object.keys(value).length > 0, 'Provide at least one field');
export const propertyStatusSchema = z.object({
    status: z.enum(['available', 'on_hold', 'let_out_or_sold', 'archived']),
    /** The day it went on hold or was let out. Today when left out. */
    on: z
        .string()
        .regex(/^\d{4}-\d{2}-\d{2}$/)
        .optional(),
});
export const PHOTO_CONTENT_TYPES = ['image/jpeg', 'image/png', 'image/webp'];
export const PHOTO_ROOMS = [
    'living',
    'bedroom',
    'kitchen',
    'bathroom',
    'balcony',
    'view',
    'building',
    'other',
];
const photoTags = {
    room: z.enum(PHOTO_ROOMS).nullable().optional(),
    /** The day the photo was taken. */
    takenOn: z
        .string()
        .regex(/^\d{4}-\d{2}-\d{2}$/)
        .nullable()
        .optional(),
};
export const photoTagSchema = z
    .object(photoTags)
    .refine((v) => v.room !== undefined || v.takenOn !== undefined, 'Give a room or a date');
export const photoUploadsSchema = z.object({
    files: z
        .array(z.object({ contentType: z.enum(PHOTO_CONTENT_TYPES), ...photoTags }))
        .min(1)
        .max(MAX_PHOTOS),
});
export const photoOrderSchema = z.object({
    ids: z.array(z.string().uuid()).min(1).max(MAX_PHOTOS),
});
export const bookVisitSchema = z.object({
    name: z.string().trim().min(2).max(80),
    phone: z.string().trim().min(1).max(20),
    slot: z.string().trim().min(1).max(60),
    consent: z.literal(true),
    /** The language of the page the client booked from. Their messages follow it. */
    lang: z.enum(['en', 'mr', 'hi']).optional(),
    /** Honeypot: a person never fills this in. */
    website: z.string().max(0).optional(),
});
/** The client's own request, changed or withdrawn from the page with the receipt the browser kept. */
export const ownVisitSchema = z.object({ receipt: z.string().trim().min(20).max(200) });
/** The phone a client types on another device to see the request made for this flat. */
export const myRequestSchema = z.object({ phone: z.string().trim().min(1).max(20) });
export const ownRescheduleSchema = ownVisitSchema.extend({
    slot: z.string().trim().min(1).max(60),
});
export const assignVisitSchema = z.object({ memberId: z.string().uuid() });
/** A time with its offset, like `2026-10-24T10:00:00+05:30`. */
const instant = z.string().datetime({ offset: true });
export const confirmVisitSchema = z.object({ at: instant.optional() }).default({});
/** The new time as words (`Sat 10:00`), as an instant (`at`), or both. An instant sets the reminders. */
export const rescheduleVisitSchema = z
    .object({ slot: z.string().trim().min(1).max(60).optional(), at: instant.optional() })
    .refine((v) => v.slot !== undefined || v.at !== undefined, 'Give a time');
//# sourceMappingURL=property.js.map